import type { AssortmentExclusionItem } from '../hooks/useAssortmentExclusions';
import { Badge, Box, Button, Menu, MenuItem, Text, Tooltip } from '../ui';
import React, {
  type CSSProperties,
  type JSX,
  type MouseEvent,
  type ReactElement,
  useMemo,
} from 'react';
import type {
  IndicatorTooltipContent,
  ShipmentIntegrationStatus,
  VoyageLeg,
  VoyagePortCall,
} from './types';
import { AlertIcon, InfoIcon, WarningAlertIcon } from '../Icons';
import { exhaustiveCheck, isDefined } from '@seaber/common/lib/generics';
import { useIntegrationRaptorDraftViolations } from '../integrations/RaptorDraft';
import { formatNumber } from '../../lib/format';
import type { ScenarioOrder } from '../../lib/planningData';
import theme from '../theme';
import type { IncomingContractType } from '../order/OrderTableUtils';
import { useIsVoyageCapacityVisibleState } from '../../state/planning/options';
import type { ClientId } from '@seaber/common/lib/clientId';
import type { TimeData } from '../core/TimeData';
import {
  isPortCallArrivalEventTimeData,
  isPortCallBerthingEventTimeData,
  isPortCallDepartureEventTimeData,
  PortCallEventType,
} from '../core/TimeData';
import {
  getPortCallSourceName,
  timelinePortCallUpdateOpts,
  usePortCallUpdates,
} from '../PortCallUpdates';
import { getEventLocation, SwapDirection } from '../core/Core';
import { formatHToDaysHour, withDefined } from '../../lib/util';
import useDropdown from '../hooks/useDropdown';
import { FloatingPortal } from '@floating-ui/react';
import { msToH } from '../../lib/time';
import { timeFieldFormat } from '../ui/TimeField';
import type { EventTimeline } from '../core/Timelines';
import {
  DischargeIndicator,
  IndicatorContainer,
  IndicatorLine,
  LoadIndicator,
  StaticIndicatorContainer,
} from './Indicators.styled';
import { Tip } from './Common.styled';
import { PortRotation } from '../PortRotation';
import type { ScenarioTimelineProps } from './ScenarioTimeline';
import { useVoyageCharterUpdates } from '../shipment/VoyageCharterUpdate';

export function OverCapacityIndicator({
  disableTooltip,
  legsOverCapacity,
}: {
  disableTooltip: boolean;
  legsOverCapacity: VoyageLeg[];
}): JSX.Element {
  const tooltipContent = (
    <>
      <Text fontWeight="bold">Vessel capacity exceeded on legs:</Text>
      {legsOverCapacity.map(
        ({
          originPortCallId,
          originPortName,
          destinationPortName,
          overCapacityMass,
          overCapacityVolume,
        }) => (
          <Box key={originPortCallId}>
            <Text>
              {originPortName} - {destinationPortName} (
              {overCapacityVolume > 0
                ? `${formatNumber(overCapacityVolume)} cbm`
                : `${formatNumber(overCapacityMass)} mts`}
              )
            </Text>
          </Box>
        ),
      )}
    </>
  );

  return (
    <WarningIndicator disableTooltip={disableTooltip}>
      {tooltipContent}
    </WarningIndicator>
  );
}

export function DraftViolationIndicator(props: {
  disableTooltip: boolean;
  portCalls: VoyagePortCall[];
}) {
  const portCallIds = useMemo(
    () => new Set(props.portCalls.map((pc) => pc.clientId).filter(isDefined)),
    [props.portCalls],
  );

  const draftViolations = useIntegrationRaptorDraftViolations(portCallIds);

  if (draftViolations.length < 1) return null;

  const overLimit = draftViolations.some(
    (violation) => violation.isDraftOverLimit,
  );

  return (
    <WarningIndicator
      iconType={overLimit ? 'alert' : 'info'}
      disableTooltip={props.disableTooltip}
    >
      <div>
        <p>
          <strong>
            {overLimit
              ? 'Predicted vessel draft exceeds port limit'
              : 'Predicted draft is within port limit'}
          </strong>
        </p>
        {draftViolations.map((violation) => (
          <div key={violation.params.portCallId}>
            <strong>{violation.params.portName}</strong>
            <p>
              Port max. draft: {formatNumber(violation.params.portDraft, 2)} m
              <br />
              Predicted draft: {formatNumber(violation.draft.fit, 2)} m<br />
              Loaded mass: {formatNumber(violation.params.loadedMass)} t
            </p>

            <p>
              <small>
                <strong>Prediction details</strong>
                <br />
                Average: {violation.draft.fit} m<br />
                Lower 2.5%: {violation.draft.lwr} m<br />
                Upper 2.5%: {violation.draft.upr} m
              </small>
            </p>
          </div>
        ))}
      </div>
    </WarningIndicator>
  );
}

export function MaxDwtViolationIndicator(props: {
  disableTooltip: boolean;
  portCalls: VoyagePortCall[];
  vesselWeightCapacity: number | null;
}) {
  const { disableTooltip, portCalls, vesselWeightCapacity } = props;

  if (!vesselWeightCapacity) {
    return null;
  }

  const violations = portCalls.filter(
    (pc) => pc.maxDwt && vesselWeightCapacity > pc.maxDwt,
  );
  if (!violations.length) {
    return null;
  }

  return (
    <WarningIndicator iconType="warning" disableTooltip={disableTooltip}>
      <div>
        <p>
          <strong>Vessel DWT exceeds port limit</strong>
        </p>
        {violations.map((violation) => (
          <div key={violation.portId}>
            <strong>{violation.portName}</strong>
            <p>
              Port Max DWT:{' '}
              {formatNumber(violation.maxDwt, 2, undefined, 'en-US')}
              <br />
              Vessel DWT:{' '}
              {formatNumber(vesselWeightCapacity, 2, undefined, 'en-US')}
            </p>
          </div>
        ))}
      </div>
    </WarningIndicator>
  );
}

export function AssortmentExclusionsIndicator({
  disableTooltip,
  assortmentExclusions,
}: {
  disableTooltip: boolean;
  assortmentExclusions: AssortmentExclusionItem[];
}) {
  const tooltipContent = (
    <>
      <Text fontWeight="bold">Check commodity exclusions for:</Text>
      {assortmentExclusions.map((exclusion) => {
        return 'vesselAttribute' in exclusion ? (
          <Box key={exclusion.exclusion.id}>
            <Text>
              {exclusion.vesselAttribute.name} - {exclusion.exclusion.name}
            </Text>
          </Box>
        ) : (
          <Box
            key={`${exclusion.previous.__typename}-${exclusion.previous.id}`}
          >
            <Text>
              {exclusion.previous.name} - {exclusion.next.name}
            </Text>
          </Box>
        );
      })}
    </>
  );

  return assortmentExclusions.length ? (
    <WarningIndicator disableTooltip={disableTooltip} iconType="warning">
      {tooltipContent}
    </WarningIndicator>
  ) : null;
}

export function UnallocatedOrdersIndicator({
  disableTooltip,
  legsWithUnallocated,
}: {
  disableTooltip: boolean;
  legsWithUnallocated: VoyageLeg[];
}): JSX.Element {
  const tooltipContent = (
    <>
      <Text fontWeight="bold">Unallocated orders on legs:</Text>
      {legsWithUnallocated.map(
        ({ originPortCallId, originPortName, destinationPortName }) => (
          <Box key={originPortCallId}>
            <Text>
              {originPortName} - {destinationPortName}
            </Text>
          </Box>
        ),
      )}
    </>
  );

  return (
    <WarningIndicator disableTooltip={disableTooltip}>
      {tooltipContent}
    </WarningIndicator>
  );
}

export type FailedRoutes = Array<{ from: string; to: string }>;

export function FailedRoutesIndicator({
  disableTooltip,
  failedRoutes,
}: {
  disableTooltip: boolean;
  failedRoutes: FailedRoutes;
}): JSX.Element | null {
  if (!failedRoutes.length) {
    return null;
  }

  const routesText = failedRoutes
    .map((r) => `${r.from} → ${r.to}`)
    .join(' and ');

  const tooltipContent = (
    <Box>
      <Text>
        Failed to load routes: <strong>{routesText}</strong>
      </Text>
    </Box>
  );

  return (
    <WarningIndicator disableTooltip={disableTooltip}>
      {tooltipContent}
    </WarningIndicator>
  );
}

function WarningIndicator({
  disableTooltip,
  children,
  iconType = 'alert',
}: {
  disableTooltip: boolean;
  children: React.ReactNode;
  iconType?: 'alert' | 'warning' | 'info';
}): JSX.Element {
  return (
    <Tooltip
      disabled={disableTooltip}
      content={<Box>{children}</Box>}
      followCursor="horizontal"
    >
      <Box
        width="13px"
        height="13px"
        display="flex"
        minWidth={13}
        alignItems="center"
        mr="2px"
      >
        {iconType === 'alert' ? (
          <AlertIcon />
        ) : iconType === 'warning' ? (
          <WarningAlertIcon />
        ) : (
          <InfoIcon />
        )}
      </Box>
    </Tooltip>
  );
}

export function DifferentFromIntegrationIndicator(props: {
  disableTooltip: boolean;
  shipmentIntegrationStatus?: ShipmentIntegrationStatus;
  shipmentId: ScenarioOrder['shipmentId'];
}) {
  const id = props.shipmentId;
  const extra = props.shipmentIntegrationStatus?.extraOrderIdsByShipment
    .get(id)
    .join(',');

  const missing = props.shipmentIntegrationStatus?.missingOrderIdsByShipment
    .get(id)
    .join(',');

  return (
    <WarningIndicator disableTooltip={props.disableTooltip}>
      <Text fontWeight="bold">
        Shipment is different from integration data,
        {extra ? ' extra: ' + extra : ''}
        {missing ? ' missing: ' + missing : ''}
      </Text>
    </WarningIndicator>
  );
}

export const indicatorTypeToColor = {
  ballast: 'voyageEvents.voyage',
  laden: 'voyageEvents.voyage',
  load: 'voyageEvents.loading',
  discharge: 'voyageEvents.discharge',
  idle: 'voyageEvents.idle',
  single: 'voyageEvents.event',
  portCall: 'voyageEvents.portCall',
};
export const indicatorTypeToLineStyle: {
  [indicatorType: string]: 'solid' | 'dashed';
} = {
  ballast: 'dashed',
  laden: 'dashed',
  load: 'solid',
  discharge: 'solid',
  idle: 'solid',
};
const contractIndicatorStyles: CSSProperties = {
  display: 'inline-block',
  flex: '0 0 16px',
  height: '16px',
  padding: '1px 0',
  fontWeight: 'bolder',
  border: '2px solid black',
  marginRight: '2px',
  lineHeight: '10px',
  fontSize: '10px',
  textAlign: 'center',
  color: 'black',
};
const inquiryIndicatorStyles: CSSProperties = {
  ...contractIndicatorStyles,
  backgroundColor: theme.colors.contractStatus.inquiry,
};
const onSubsIndicatorStyles: CSSProperties = {
  ...contractIndicatorStyles,
  backgroundColor: theme.colors.contractStatus.onSubs,
};
const subsExpiredIndicatorStyles: CSSProperties = {
  ...contractIndicatorStyles,
  backgroundColor: theme.colors.contractStatus.subsExpired,
};

const deckCargoIndicatorStyles: CSSProperties = {
  ...contractIndicatorStyles,
  backgroundColor: theme.colors.neutral.white_50,
};

export function IncomingContractIndicator({
  contractType,
}: {
  contractType: IncomingContractType;
}): JSX.Element | null {
  if (contractType === 'INQUIRY') {
    return <span style={inquiryIndicatorStyles}>i</span>;
  }

  if (contractType === 'PRELIMINARY') {
    return <span style={inquiryIndicatorStyles}>P</span>;
  }

  if (contractType === 'ON_SUBS') {
    return <span style={onSubsIndicatorStyles}>s</span>;
  }

  if (contractType === 'SUBS_EXPIRED') {
    return <span style={subsExpiredIndicatorStyles}>s</span>;
  }

  return null;
}

export function DeckCargoIndicator(): JSX.Element | null {
  return <span style={deckCargoIndicatorStyles}>d</span>;
}

export function AvailableCapacityIndicator({
  disableTooltip,
  leg,
}: {
  disableTooltip: boolean;
  leg: VoyageLeg;
}): JSX.Element | null {
  const [isVisible] = useIsVoyageCapacityVisibleState();

  const {
    availableMassCapacity,
    availableMassCapacityPercentage,
    availableVolumeCapacity,
    destinationPortName,
    originPortName,
    availableVolumeCapacityPercentage,
  } = leg;

  if (!isVisible) {
    return null;
  }

  const isMassDataAvailable =
    availableMassCapacity !== null && availableMassCapacityPercentage !== null;
  const isVolumeDataAvailable =
    availableVolumeCapacity !== null &&
    availableVolumeCapacityPercentage !== null;

  if (!isMassDataAvailable && !isVolumeDataAvailable) {
    return null;
  }

  let limitingCapacity: 'mass' | 'volume' = 'mass';

  if (isMassDataAvailable && isVolumeDataAvailable) {
    limitingCapacity =
      availableVolumeCapacityPercentage < availableMassCapacityPercentage
        ? 'volume'
        : 'mass';
  } else if (isMassDataAvailable && !isVolumeDataAvailable) {
    limitingCapacity = 'mass';
  } else if (!isMassDataAvailable && isVolumeDataAvailable) {
    limitingCapacity = 'volume';
  }

  const value =
    limitingCapacity === 'mass'
      ? availableMassCapacity!
      : availableVolumeCapacity!;
  const percentageValue =
    limitingCapacity === 'mass'
      ? availableMassCapacityPercentage!
      : availableVolumeCapacityPercentage!;

  const fontWeight = percentageValue <= 30 ? 'normal' : 'bold';
  const opacity = percentageValue <= 10 ? 0.5 : 1;
  const showSup = limitingCapacity === 'volume';
  const displayedValue = (
    <>
      <>{formatNumber(value / 1000)}</>
      {showSup && <sup>*</sup>}
    </>
  );

  const tooltipContent = (
    <Box display="flex" flexDirection="column">
      <Text fontWeight="bold">
        {originPortName} - {destinationPortName}
      </Text>
      <Text>
        Available mass capacity: {formatNumber(availableMassCapacity)} mts
      </Text>
      <Text>
        <span
          style={{
            position: 'relative',
            top: '-0.2em',
            fontSize: '0.85em',
            marginRight: '0.1em',
          }}
        >
          *
        </span>
        Available volume capacity: {formatNumber(availableVolumeCapacity)} cbm
      </Text>
    </Box>
  );

  return (
    <Tooltip disabled={disableTooltip} content={tooltipContent}>
      <Text color="neutral.white_50" fontWeight={fontWeight} opacity={opacity}>
        {displayedValue}
      </Text>
    </Tooltip>
  );
}

const stopPropagation = (e: React.MouseEvent) => {
  e.stopPropagation();
  e.nativeEvent.stopImmediatePropagation();
};
export const PortCallIndicator = ({
  portCallId,
  portName,
  children,
  events,
  locations,
}: {
  portCallId: ClientId | null;
  portName: string;
  children: JSX.Element;
  events: TimeData[];
  locations: ScenarioTimelineProps['scheduleContext']['locations'];
}) => {
  const arrival = useMemo(
    () => events.find(isPortCallArrivalEventTimeData),
    [events],
  );
  const berthing = useMemo(
    () => events.find(isPortCallBerthingEventTimeData),
    [events],
  );
  const departure = useMemo(
    () => events.find(isPortCallDepartureEventTimeData),
    [events],
  );

  const { updates, applyPortCallUpdate, ignorePortCallUpdates } =
    usePortCallUpdates(
      portCallId ? [portCallId] : [],
      timelinePortCallUpdateOpts,
    );

  const validUpdates = useMemo(
    () =>
      updates
        .map((update) => {
          const relatedEvent =
            update.type === PortCallEventType.Arrival
              ? arrival
              : update.type === PortCallEventType.Berthing
                ? berthing
                : update.type === PortCallEventType.Departure
                  ? departure
                  : exhaustiveCheck(update.type, `Unexpected port call type`);

          const port = relatedEvent
            ? getEventLocation(relatedEvent.event, locations)
            : null;
          const minValue =
            relatedEvent === departure
              ? (withDefined(berthing?.start, (x) => x + 500) ?? 0)
              : (relatedEvent?.previousEventStart ?? 0);
          if (update.value > minValue) {
            return {
              ...update,
              timezone: port?.timezone ?? undefined,
              diff: relatedEvent?.start
                ? update.value - relatedEvent?.start
                : 0,
            };
          } else {
            return null;
          }
        })
        .filter(isDefined)
        .filter((update) => {
          // Not interested in tiny updates
          return Math.abs(update.diff) > 30 * 1000;
        })
        .sort((updateA, updateB) => {
          const typeToPriority = (type: PortCallEventType) =>
            type === PortCallEventType.Arrival
              ? 3
              : type === PortCallEventType.Berthing
                ? 2
                : type === PortCallEventType.Departure
                  ? 1
                  : exhaustiveCheck(type, `Unexpected port call type`);

          return (
            typeToPriority(updateB.type) - typeToPriority(updateA.type) ||
            (updateB.isConfirmed ? 1 : 0) - (updateA.isConfirmed ? 1 : 0) ||
            updateB.updatedAt - updateA.updatedAt
          );
        }),
    [updates, arrival, berthing, departure, locations],
  );

  const {
    getMenuProps,
    getTargetProps,
    isVisible,
    setMenuRef,
    setTargetRef,
    setVisibility,
    styles,
  } = useDropdown();

  const handleClick = (e: React.MouseEvent) => {
    stopPropagation(e);
    setVisibility(!isVisible);
  };

  const MINOR_UPDATE_THRESHOLD_HOURS = 4;
  const updateTypeTextMap = {
    [PortCallEventType.Arrival]: 'Arrival (ETA)',
    [PortCallEventType.Berthing]: 'Berthing (ETB)',
    [PortCallEventType.Departure]: 'Departure (ETD)',
  };

  const isOnlyMinorUpdates = useMemo(() => {
    const confirmed = new Set<PortCallEventType>();
    for (const update of validUpdates) {
      if (update.isConfirmed) {
        confirmed.add(update.type);
      }
    }
    // If there is a confirmed update of the same type then keep only confirmed updates
    return validUpdates
      .filter((update, _index) =>
        confirmed.has(update.type) ? update.isConfirmed : true,
      )
      .every(
        (update) =>
          Math.abs(update.diff) < MINOR_UPDATE_THRESHOLD_HOURS * 60 * 60 * 1000,
      );
  }, [validUpdates]);

  return portCallId && validUpdates.length > 0 ? (
    <>
      <Box
        ref={setTargetRef}
        bg={
          isOnlyMinorUpdates
            ? '#cf874b'
            : isVisible
              ? 'orange.orange_700'
              : 'orange.orange_600'
        }
        onClick={handleClick}
        onMouseDown={stopPropagation}
        pr={2}
        {...getTargetProps()}
      >
        {children}
      </Box>
      <FloatingPortal id="dropdown-root">
        <div
          ref={setMenuRef}
          style={styles}
          {...getMenuProps({
            onClick: stopPropagation,
            onMouseDown: stopPropagation,
          })}
        >
          {isVisible && (
            <Menu hasShadow>
              <Box p={3}>
                <Text fontSize={1} fontWeight="bold" color="neutral.gray_340">
                  Updates to port call at {portName}
                </Text>
              </Box>
              {validUpdates.map((update) => {
                const hoursDiff = msToH(update.diff);
                const diffText =
                  (hoursDiff >= 0 ? '+' : '') + hoursDiff.toFixed(1) + 'h';
                return (
                  <MenuItem
                    key={`${update.portCallId}${update.source}${update.type}${update.value}`}
                    label={
                      `${update.isConfirmed ? '✓ ' : ''}` +
                      `${updateTypeTextMap[update.type]} ${diffText} ` +
                      `(` +
                      getPortCallSourceName(update.source) +
                      ` ` +
                      `${update.sourceType}): ${timeFieldFormat(
                        update.value,
                        update.timezone,
                      )}`
                    }
                    onPress={() => applyPortCallUpdate(update)}
                  />
                );
              })}
              <Box p={3}>
                <Button
                  variant="secondary"
                  onPress={() => {
                    ignorePortCallUpdates([portCallId]);
                    // Looks like this component prevents click events from bubbling
                    setVisibility(false);
                  }}
                >
                  Ignore updates
                </Button>
              </Box>
            </Menu>
          )}
        </div>
      </FloatingPortal>
    </>
  ) : (
    children
  );
};
export const BottomIndicators = React.memo(function BottomIndicators(props: {
  zIndex: CSSProperties['zIndex'];
  staticIndicators: JSX.Element[];
  staticViolationIndicators: JSX.Element | JSX.Element[] | null;
  dragging: boolean | undefined;
  isShared: undefined | boolean;
  selected: boolean | undefined;
  showLoadToLeftSwap: boolean;
  onSwapScheduleEntry:
    | ((
        timelineId: EventTimeline['id'],
        indexToMove: number,
        direction: SwapDirection,
        timeDataItem: TimeData,
      ) => void)
    | undefined;
  firstLoad: TimeData | undefined;
  firstLoadIndex: number | undefined;
  timelineId: string;
  showLoadToRightSwap: boolean | undefined;
  showDischargeToLeftSwap: boolean | undefined;
  lastDischarge: TimeData | undefined;
  lastDischargeIndex: number | undefined;
  showDischargeToRightSwap: boolean;
}) {
  const {
    zIndex,
    staticIndicators,
    staticViolationIndicators,
    dragging,
    isShared,
    selected,
    showLoadToLeftSwap,
    onSwapScheduleEntry,
    firstLoad,
    firstLoadIndex,
    timelineId,
    showLoadToRightSwap,
    showDischargeToLeftSwap,
    lastDischarge,
    lastDischargeIndex,
    showDischargeToRightSwap,
  } = props;
  return (
    <>
      <Box
        display="flex"
        flexDirection="row"
        position="absolute"
        bottom={0}
        height="4px"
        zIndex={zIndex}
      >
        {staticIndicators}
      </Box>
      <Box position="absolute" bottom="20px" height="4px">
        {staticViolationIndicators}
      </Box>
      {!dragging && !isShared && selected && (
        <>
          <LoadIndicator
            style={{
              zIndex,
              display: 'flex',
              justifyContent: 'center',
              bottom: 'unset',
              top: '35%',
              left: '-10px',
            }}
          >
            <div
              style={{
                position: 'absolute',
                display: 'flex',
              }}
            >
              {showLoadToLeftSwap ? (
                <PortRotation
                  direction="left"
                  onPress={() => {
                    if (onSwapScheduleEntry && firstLoad && firstLoadIndex) {
                      onSwapScheduleEntry(
                        timelineId,
                        firstLoadIndex,
                        SwapDirection.Left,
                        firstLoad,
                      );
                    }
                  }}
                />
              ) : null}
              {showLoadToRightSwap ? (
                <PortRotation
                  direction="right"
                  onPress={() => {
                    if (onSwapScheduleEntry && firstLoad && firstLoadIndex) {
                      onSwapScheduleEntry(
                        timelineId,
                        firstLoadIndex,
                        SwapDirection.Right,
                        firstLoad,
                      );
                    }
                  }}
                />
              ) : null}
            </div>
          </LoadIndicator>

          <DischargeIndicator
            style={{
              zIndex,
              display: 'flex',
              justifyContent: 'center',
              bottom: 'unset',
              top: '35%',
              right: '-10px',
            }}
          >
            <div
              style={{
                position: 'absolute',
                display: 'flex',
                marginLeft: 18,
              }}
            >
              {showDischargeToLeftSwap ? (
                <PortRotation
                  direction="left"
                  onPress={() => {
                    if (
                      onSwapScheduleEntry &&
                      lastDischarge &&
                      lastDischargeIndex
                    ) {
                      onSwapScheduleEntry(
                        timelineId,
                        lastDischargeIndex,
                        SwapDirection.Left,
                        lastDischarge,
                      );
                    }
                  }}
                />
              ) : null}
              {showDischargeToRightSwap ? (
                <PortRotation
                  direction="right"
                  onPress={() => {
                    if (
                      onSwapScheduleEntry &&
                      lastDischarge &&
                      lastDischargeIndex
                    ) {
                      onSwapScheduleEntry(
                        timelineId,
                        lastDischargeIndex,
                        SwapDirection.Right,
                        lastDischarge,
                      );
                    }
                  }}
                />
              ) : null}
            </div>
          </DischargeIndicator>
        </>
      )}
    </>
  );
});

interface TimelineIndicatorProps {
  color?: string;
  lineStyle?: 'solid' | 'dashed';
  offset?: number;
  position?: 'center' | 'bottom';
  tooltip: IndicatorTooltipContent;
  width: string | number;
  zIndex?: CSSProperties['zIndex'];
  opacity?: number;
  tooltipDisabled?: boolean;
  indicatorLineWidth?: string | number;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const IndicatorToolTip = React.memo(
  ({
    duration,
    startTime,
    title,
    portName,
    assortmentName,
    timezone,
    from,
    to,
    amount,
    amountUnit,
    totalDuration,
    assortments,
    type,
  }: IndicatorTooltipContent): JSX.Element => {
    const formattedStartTime = startTime
      ? timeFieldFormat(startTime, timezone)
      : undefined;

    return (
      <Tip>
        <Text
          fontWeight="bold"
          color="neutral.gray_700"
          mb={startTime || duration ? 1 : 0}
        >
          {title}
        </Text>
        {portName ? (
          <Text color="neutral.gray_430">Port: {portName}</Text>
        ) : null}
        {from ? <Text color="neutral.gray_430">From: {from}</Text> : null}
        {to ? <Text color="neutral.gray_430">To: {to}</Text> : null}
        {startTime ? (
          <Text color="neutral.gray_430">Start time: {formattedStartTime}</Text>
        ) : null}
        {assortmentName ? (
          <Text color="neutral.gray_430">Commodity: {assortmentName}</Text>
        ) : null}
        {amount && amountUnit ? (
          <Text color="neutral.gray_430">
            Amount: {formatNumber(amount)} {amountUnit}
          </Text>
        ) : null}
        {assortments &&
          assortments.map((x, i) => (
            <Box key={i} display="flex" flexDirection="column">
              <Text>Assortment: {x.assortmentName}</Text>
              <Text>
                Amount: {formatNumber(x.amount)} {x.amountUnit}
              </Text>
            </Box>
          ))}
        {totalDuration || duration ? (
          type ? (
            type === 'load_violation' ? (
              <Text color="neutral.gray_430">
                Arriving {Number(duration ?? 0) > 0 ? 'late' : 'early'} by:{' '}
                {formatHToDaysHour(Math.abs(duration ?? 0))}
              </Text>
            ) : (
              <Text color="neutral.gray_430">
                Departing {Number(duration ?? 0) > 0 ? 'late' : 'early'} by:{' '}
                {formatHToDaysHour(Math.abs(duration ?? 0))}
              </Text>
            )
          ) : (
            <Text color="neutral.gray_430">
              Total Duration:{' '}
              {formatHToDaysHour(totalDuration || duration || 0)}
            </Text>
          )
        ) : null}
      </Tip>
    );
  },
);
const preventDefault = (e: MouseEvent) => e.preventDefault();
const indicatorAlignments = {
  bottom: 'flex-end',
  center: 'center',
};
export const TimelineLineIndicator = React.memo(
  ({
    color,
    lineStyle = 'dashed',
    offset = 0,
    position = 'center',
    tooltip,
    width,
    zIndex,
  }: TimelineIndicatorProps): ReactElement => {
    return (
      <IndicatorContainer
        style={{
          zIndex: zIndex,
          left: offset + 'px',
          width: width + 'px',
          maxWidth: width + 'px',
          alignItems: indicatorAlignments[position],
        }}
        onClick={preventDefault}
      >
        <Tooltip
          content={<IndicatorToolTip {...tooltip} />}
          followCursor="horizontal"
        >
          <IndicatorLine
            style={{
              width: width,
              maxWidth: width,
              borderBottomStyle: lineStyle,
            }}
            onClick={preventDefault}
            borderBottomColor={color}
          />
        </Tooltip>
      </IndicatorContainer>
    );
  },
);

export const StaticLineIndicator = React.memo(
  ({
    color,
    lineStyle = 'dashed',
    offset = 0,
    tooltip,
    width,
    opacity,
    tooltipDisabled,
    onMouseEnter,
    onMouseLeave,
    indicatorLineWidth,
  }: TimelineIndicatorProps): ReactElement => {
    return (
      <StaticIndicatorContainer
        style={{
          left: offset + 'px',
          width: width + 'px',
          maxWidth: width + 'px',
          position: 'absolute',
          top: '-4px',
          height: '8px',
        }}
        onClick={preventDefault}
      >
        <Tooltip
          disabled={tooltipDisabled}
          content={<IndicatorToolTip {...tooltip} />}
          followCursor="horizontal"
        >
          <IndicatorLine
            style={{
              width: width,
              maxWidth: width,
              borderBottomStyle: lineStyle,
              opacity: opacity || 1,
            }}
            onClick={preventDefault}
            borderBottomColor={color}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            borderBottomWidth={indicatorLineWidth}
          />
        </Tooltip>
      </StaticIndicatorContainer>
    );
  },
);

export const PortNameIndicator = ({
  children,
  portName,
}: {
  children?: React.ReactNode;
  portName?: string;
}) => {
  if (portName && children) {
    throw new Error('Cannot pass both children and portName');
  }
  return (
    <Text
      isUnselectable
      isSingleLine
      fontSize={2}
      color="neutral.white_50"
      overflow="hidden"
      px={2}
      display="flex"
      flexDirection="row"
      position="relative"
      verticalAlign="bottom"
      alignItems="center"
      data-testid="portNameIndicator"
    >
      {children ? children : null}
      {portName ? portName : null}
    </Text>
  );
};

interface VoyageCharterUpdateIndicatorProps {
  voyageCharterId: number;
}

export const VoyageCharterUpdateIndicator = ({
  voyageCharterId,
}: VoyageCharterUpdateIndicatorProps): JSX.Element | null => {
  const { updatesByVoyageCharter } = useVoyageCharterUpdates();

  const updates = updatesByVoyageCharter.get(voyageCharterId) ?? [];

  if (updates.length < 1) {
    return null;
  }

  return (
    <Tooltip content={<Text>{updates.length} pending change requests</Text>}>
      <Badge isRounded variant="past">
        Change requested
      </Badge>
    </Tooltip>
  );
};
